import { useEffect } from 'react';
import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Boxes,
  Truck,
  ClipboardList,
} from 'lucide-react';
import { AppShell } from '@avagency/frontend/components/shell/AppShell';
import { Dashboard } from './pages/Dashboard';
import { InventoryHealth } from './pages/InventoryHealth';
import { SupplierList } from './pages/SupplierList';
import { PurchaseOrders } from './pages/PurchaseOrders';
import { PODetail } from './pages/PODetail';
import { ConnectShopify } from './pages/ConnectShopify';

// PO Pilot — supplier-aware purchase order management for sub-$30K MRR Shopify stores.
//
// Phase 11 app shell. Auth gate (WorkOS useMe + SignIn) lands in Phase 2;
// for now the dev Vite proxy injects X-AV-Tenant-ID so all API calls are
// tenant-scoped without a real session cookie. The shell is wired and
// ready to drop the auth gate in once Phase 2 routes exist.
export function App() {
  useEffect(() => {
    document.title = 'PO Pilot';
  }, []);

  return (
    <AppShell
      productSlug="po-pilot"
      topbar={<TopbarBrand />}
      sidebar={<Sidebar />}
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/inventory" element={<InventoryHealth />} />
        <Route path="/suppliers" element={<SupplierList />} />
        <Route path="/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/purchase-orders/:id" element={<PODetail />} />
        <Route path="/connect" element={<ConnectShopify />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function TopbarBrand() {
  return (
    <div className="flex items-center gap-2.5">
      {/* Green logo mark — matches LP nav-orb treatment */}
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{
          background: 'linear-gradient(180deg, hsl(153 39% 56%), hsl(153 39% 40%) 60%, hsl(153 45% 30%))',
          boxShadow: '0 2px 8px hsl(153 39% 49% / .35), inset 0 1px 0 hsl(0 0% 100% / .25)',
        }}
        aria-hidden
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          {/* Simple PO/clipboard mark */}
          <rect x="3" y="1.5" width="8" height="11" rx="1.5" fill="hsl(222 67% 5%)" opacity=".9" />
          <rect x="5" y="4" width="4" height="1.2" rx=".6" fill="hsl(222 67% 5%)" opacity=".6" />
          <rect x="5" y="6.2" width="5.5" height="1.2" rx=".6" fill="hsl(222 67% 5%)" opacity=".6" />
          <rect x="5" y="8.4" width="3.5" height="1.2" rx=".6" fill="hsl(222 67% 5%)" opacity=".6" />
        </svg>
      </div>
      <span className="text-base font-semibold tracking-tight">PO Pilot</span>
      <span className="hidden text-xs text-muted-foreground sm:inline">
        Supplier-aware POs for Shopify
      </span>
    </div>
  );
}

function Sidebar() {
  const linkClass = (isActive: boolean) =>
    [
      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-foreground hover:bg-accent hover:text-accent-foreground',
    ].join(' ');

  return (
    <ul className="space-y-1">
      <li>
        <NavLink to="/" end className={({ isActive }) => linkClass(isActive)}>
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </NavLink>
      </li>
      <li>
        <NavLink to="/inventory" className={({ isActive }) => linkClass(isActive)}>
          <Boxes className="h-4 w-4" />
          Inventory
        </NavLink>
      </li>
      <li>
        <NavLink to="/suppliers" className={({ isActive }) => linkClass(isActive)}>
          <Truck className="h-4 w-4" />
          Suppliers
        </NavLink>
      </li>
      <li>
        <NavLink to="/purchase-orders" className={({ isActive }) => linkClass(isActive)}>
          <ClipboardList className="h-4 w-4" />
          Purchase Orders
        </NavLink>
      </li>
    </ul>
  );
}
